import React from 'react';
import { RefreshCw, Check } from 'react-feather';
import { GridAction, GridField, GridFilter } from './gridTypes';
import { UiButton } from '../../UiButton';

const ROUTE_ACTION_VARIANTS: Record<string, string> = {
    'grid-action-add':    'verde',
    'grid-action-edit':   'azul',
    'grid-action-delete': 'danger',
};

function parseActionClass(cls: string): { variant: string; size?: 'sm' | 'lg'; outline: boolean } {
    const routeVariant = ROUTE_ACTION_VARIANTS[cls.trim()];
    if (routeVariant) return { variant: routeVariant, size: 'sm', outline: false };

    const parts = cls.split(' ');
    const outline = parts.some(p => p.startsWith('btn-outline-'));
    const size = parts.includes('btn-sm') ? 'sm' : parts.includes('btn-lg') ? 'lg' : undefined;
    const variantPart = parts.find(p =>
        p.startsWith('btn-outline-') || (p.startsWith('btn-') && p !== 'btn-sm' && p !== 'btn-lg' && p !== 'btn')
    );
    const variant = variantPart
        ? variantPart.replace('btn-outline-', '').replace('btn-', '')
        : 'secondary';
    return { variant, size, outline };
}

interface GridActionsProps {
    allActions: GridAction[];
    hideDefaultActions: boolean;
    refreshData: () => void;
    handleAction: (action: GridAction) => void;
    isActionEnabled: (action: GridAction) => boolean;
    getActionIcon: (action: GridAction) => React.ReactNode;
    getActionClass: (action: GridAction) => string;
    filtrableFields: GridField[];
    pendingFilters: GridFilter[];
    addFilter: (fieldName: string) => void;
    selectionMode?: 'none' | 'single' | 'multiple';
    triggerSelect?: (data: any) => void;
    firstSelectedRow?: any;
    selectedRowsArray?: any[];
}

interface GridSelectionActionsProps {
    selectionMode: 'none' | 'single' | 'multiple';
    triggerSelect?: (data: any) => void;
    firstSelectedRow: any;
    selectedRowsArray: any[];
}

export function GridActions({
    allActions,
    hideDefaultActions,
    refreshData,
    handleAction,
    isActionEnabled,
    getActionIcon,
    getActionClass,
    filtrableFields,
    pendingFilters,
    addFilter,
    selectionMode,
    triggerSelect,
    firstSelectedRow,
    selectedRowsArray = [],
}: GridActionsProps) {
    const hasSelect = selectionMode !== 'none' && !!triggerSelect;

    if (hideDefaultActions && !hasSelect) {
        return null;
    }

    return (
        <div className="grid-actions">
            {hasSelect && (
                <UiButton
                    type="button"
                    variant="azul-ceu"
                    size="sm"
                    onClick={() => triggerSelect!(selectionMode === 'single' ? firstSelectedRow : selectedRowsArray)}
                    disabled={selectedRowsArray.length === 0}
                >
                    <Check size={12} /> Selecionar {selectedRowsArray.length > 1 ? `(${selectedRowsArray.length})` : ''}
                </UiButton>
            )}

            {!hideDefaultActions && <>
                <select
                    className="grid-filter-select"
                    value=""
                    onChange={(e) => { if (e.target.value) addFilter(e.target.value); }}
                >
                    <option value="">+ Filtro</option>
                    {filtrableFields.filter(f => !pendingFilters.find(fil => fil.name === f.name)).map(f => (
                        <option key={f.name} value={f.name}>{f.header}</option>
                    ))}
                </select>

                <UiButton type="button" variant="zinc" size="sm" outline onClick={refreshData}>
                    <RefreshCw size={12} /> Atualizar
                </UiButton>

                {allActions.map((action, idx) => {
                    const { variant, size, outline } = parseActionClass(getActionClass(action));
                    return (
                        <UiButton
                            key={action.route || idx}
                            type="button"
                            variant={variant}
                            size={size ?? 'sm'}
                            outline={outline}
                            onClick={() => handleAction(action)}
                            disabled={!isActionEnabled(action)}
                        >
                            {getActionIcon(action)}{action.title}
                        </UiButton>
                    );
                })}
            </>}

        </div>
    );
}

export function GridSelectionActions(_props: GridSelectionActionsProps) {
    return null;
}
